import React, { useEffect, useState } from "react";
import {
  fetchDisputeCategories,
  openDisputeV2,
  fetchDisputeThread,
  respondToDispute,
} from "../lib/api";

/**
 * DisputeMatchModal — Wave C UI for filing OR responding to a match dispute.
 *
 * Two modes:
 *   - File new dispute: pick a category, write a reason, optionally drop an
 *     evidence URL (YouTube/Twitch/Streamable/Medal/Imgur/Discord).
 *   - View / respond: when a dispute already exists on this challenge, the
 *     modal opens directly on the read-only summary + a one-shot response box.
 *
 * Backend enforces who-can-do-what; the modal just renders the affordances.
 */
export function DisputeMatchModal({ challenge, onClose, onToast, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [thread, setThread] = useState(null);
  const [mode, setMode] = useState("file");   // "file" | "view"
  const [busy, setBusy] = useState(false);

  const [category, setCategory] = useState("wrong_score");
  const [reason, setReason] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");

  const [responseMsg, setResponseMsg] = useState("");
  const [responseEvidence, setResponseEvidence] = useState("");

  useEffect(() => { fetchDisputeCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => {
    if (!challenge?.challenge_id) return;
    fetchDisputeThread(challenge.challenge_id)
      .then((t) => {
        setThread(t);
        setMode(t?.disputed ? "view" : "file");
      })
      .catch(() => { setMode("file"); });
  }, [challenge?.challenge_id]);

  const submitNew = async () => {
    setBusy(true);
    try {
      await openDisputeV2(challenge.challenge_id, {
        category, reason: reason.trim(), evidence_url: evidenceUrl.trim() || null,
      });
      onToast?.("⚖ Dispute filed. The other captain has been notified.");
      const refreshed = await fetchDisputeThread(challenge.challenge_id);
      setThread(refreshed); setMode("view");
      onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't file dispute.");
    } finally { setBusy(false); }
  };

  const submitResponse = async () => {
    if (!responseMsg.trim()) return;
    setBusy(true);
    try {
      await respondToDispute(challenge.challenge_id, {
        message: responseMsg.trim(),
        evidence_url: responseEvidence.trim() || null,
      });
      const refreshed = await fetchDisputeThread(challenge.challenge_id);
      setThread(refreshed);
      setResponseMsg(""); setResponseEvidence("");
      onToast?.("Response posted to the dispute thread.");
      onChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Response failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
         onClick={onClose}
         data-testid="dispute-modal-backdrop">
      <div className="glass neon-glow-pink rounded-3xl p-6 w-full max-w-2xl mx-auto my-12"
           onClick={(e) => e.stopPropagation()}
           data-testid="dispute-modal">

        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-scale-balanced mr-1" />
              {mode === "view" ? "DISPUTE THREAD" : "FILE A DISPUTE"}
            </p>
            <h2 className="font-display text-2xl grad-text mt-0.5">Match Outcome</h2>
            {thread?.team_a && thread?.team_b && (
              <p className="text-[12px] text-white/55 mt-1">
                <span className="text-[#14b8a6]">{thread.team_a.name}</span>
                <span className="mx-2 font-bold tabular-nums text-white">
                  {thread.score_a}-{thread.score_b}
                </span>
                <span className="text-[#14b8a6]">{thread.team_b.name}</span>
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="dispute-close"
                  className="text-white/60 hover:text-white text-2xl px-2">
            <i className="fas fa-times" />
          </button>
        </div>

        {/* ──────────────────────── VIEW / RESPONSE ──────────────────────── */}
        {mode === "view" && thread && (
          <div className="space-y-4">
            <div className="rounded-2xl p-4 bg-[#14b8a6]/10 border border-[#14b8a6]/30">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
                <i className="fas fa-flag mr-1" />OPENED — {thread.category_label || thread.category || "Other"}
              </p>
              {thread.reason && <p className="text-sm text-white/85 mt-2 whitespace-pre-wrap">{thread.reason}</p>}
              {thread.evidence_url && (
                <a href={thread.evidence_url} target="_blank" rel="noreferrer"
                   data-testid="dispute-evidence-link"
                   className="inline-flex items-center gap-1 text-[12px] text-[#14b8a6] hover:underline mt-2">
                  <i className="fas fa-link" /> View evidence
                </a>
              )}
            </div>

            {(thread.responses || []).map((r, i) => (
              <div key={i} className="rounded-2xl p-3 bg-black/40 border border-white/10"
                   data-testid={`dispute-response-${i}`}>
                <p className="text-[11px] tracking-widest text-[#14b8a6] font-bold">
                  {r.user_name || "Captain"}
                </p>
                <p className="text-sm text-white/85 mt-1 whitespace-pre-wrap">{r.message}</p>
                {r.evidence_url && (
                  <a href={r.evidence_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[12px] text-[#14b8a6] hover:underline mt-2">
                    <i className="fas fa-link" /> Evidence
                  </a>
                )}
              </div>
            ))}

            <div className="rounded-2xl p-3 bg-black/40 border border-[#14b8a6]/30">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
                <i className="fas fa-reply mr-1" />POST RESPONSE
              </p>
              <textarea
                value={responseMsg}
                onChange={(e) => setResponseMsg(e.target.value)}
                placeholder="Add your side of the story…"
                rows={3}
                data-testid="dispute-response-input"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30"
              />
              <input
                value={responseEvidence}
                onChange={(e) => setResponseEvidence(e.target.value)}
                placeholder="Optional: evidence URL (YouTube/Twitch/Streamable/Medal/Imgur/Discord)"
                data-testid="dispute-response-evidence"
                className="w-full mt-2 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30"
              />
              <div className="mt-2 flex justify-end">
                <button type="button" onClick={submitResponse}
                        disabled={busy || !responseMsg.trim()}
                        data-testid="dispute-response-submit"
                        className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-50">
                  {busy ? "…" : "POST RESPONSE"}
                </button>
              </div>
            </div>

            <p className="text-[10px] text-white/40 text-center">
              An admin will review this dispute and may flip / void the result.
            </p>
          </div>
        )}

        {/* ──────────────────────── FILE NEW ──────────────────────── */}
        {mode === "file" && (
          <div className="space-y-4">
            <p className="text-[12px] text-white/55">
              Pick the closest reason. Add detail + an optional clip/screenshot
              so an admin can resolve quickly.
            </p>

            <div>
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1.5">CATEGORY</p>
              <div className="flex flex-wrap gap-2" data-testid="dispute-category-chips">
                {categories.map((c) => (
                  <button
                    type="button" key={c.id}
                    onClick={() => setCategory(c.id)}
                    data-testid={`dispute-category-${c.id}`}
                    className={`text-[11px] tracking-widest px-3 py-1.5 rounded-full border transition ${
                      category === c.id
                        ? "bg-[#14b8a6]/25 border-[#14b8a6] text-[#14b8a6]"
                        : "bg-black/30 border-white/15 text-white/70 hover:border-white/40"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1.5">DETAILS</p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Tell admin what happened — keep it factual."
                data-testid="dispute-reason-input"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30"
              />
            </div>

            <div>
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1.5">
                EVIDENCE URL <span className="text-white/35 font-normal">(optional)</span>
              </p>
              <input
                value={evidenceUrl}
                onChange={(e) => setEvidenceUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… · clips.twitch.tv/… · medal.tv/…"
                data-testid="dispute-evidence-input"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30"
              />
              <p className="text-[10px] text-white/40 mt-1">
                Hosts allowed: YouTube · Twitch · Streamable · Medal.tv · Imgur · Discord CDN
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                      data-testid="dispute-cancel"
                      className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">CANCEL</button>
              <button type="button" onClick={submitNew} disabled={busy}
                      data-testid="dispute-submit"
                      className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
                {busy ? "…" : "FILE DISPUTE"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DisputeMatchModal;
